// src/components/communs/FormationVideo.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Clock, ChevronLeft, ChevronRight,
    Maximize2, Minimize2, Play, Pause, Volume2, VolumeX,
    BookOpen, ChevronDown, ChevronUp, LogOut, Home,
    SkipForward, SkipBack
} from 'lucide-react';

export default function FormationVideo({
    module,
    videoUrl,
    formationTitre,
    moduleOrdre,
    totalModules,
    onClose,
    isOpen,
    onModuleChange,
    onComplete,
    mode = 'production',
}) {
    console.log('🎬 [VIDEO] Composant rendu - mode:', mode, 'videoUrl:', videoUrl);

    const [moduleKey, setModuleKey] = useState(module?.id);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [progress, setProgress] = useState(0);
    const [timeSpent, setTimeSpent] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [videoError, setVideoError] = useState(null);
    const [loadAttempts, setLoadAttempts] = useState(0);
    const [isBuffering, setIsBuffering] = useState(false);
	const [loadedVideoUrl, setLoadedVideoUrl] = useState(videoUrl);
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const timerRef = useRef(null);
    const saveIntervalRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const loadProgressForModule = useCallback((moduleId) => {
        if (!moduleId) return { time: 0, progress: 0 };
        const saved = localStorage.getItem(`module_progress_${moduleId}`);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                return {
                    time: data.timeSpent || 0,
                    progress: data.progress || 0
                };
            } catch (e) {
                console.error("[FormationVideo]Erreur chargement progression:", e);
            }
        }
        return { time: 0, progress: 0 };
    }, []);

	if (videoUrl && videoUrl !== loadedVideoUrl) {
	    setLoadedVideoUrl(videoUrl);
	    setIsLoading(true);
	    setVideoError(null);
	    setLoadAttempts(0);
	}
	
    if (module?.id && module.id !== moduleKey) {
        console.log('🎬 [VIDEO] Module changé, chargement progression');
        const initial = loadProgressForModule(module.id);
        setModuleKey(module.id);
        setTimeSpent(initial.time);
        setProgress(initial.progress);
        setCurrentTime(0);
        setDuration(0);
        setIsLoading(true);
        setVideoError(null);
        setLoadAttempts(0);
        setIsPlaying(false);
    }
	
	useEffect(() => {
	    if (videoRef.current && videoUrl) {
	        videoRef.current.load();
	    }
	}, [videoUrl]);
	
	
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '00:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const saveProgressToLocalStorage = useCallback(() => {
        if (mode === 'demo') return;
        if (module?.id && !isLoading && duration > 0) {
            const progressValue = Math.min(100, Math.round((currentTime / duration) * 100));
            const progressData = {
                moduleId: module.id,
                timeSpent: timeSpent,
                progress: progressValue,
                currentTime: currentTime,
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(progressData));
            setProgress(prev => prev !== progressValue ? progressValue : prev);
            console.log(`✅ Progression vidéo sauvegardée: ${progressValue}% pour le module ${module.id}`);
        }
    }, [module, currentTime, timeSpent, duration, isLoading, mode]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isLoading && module?.id && duration > 0) {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => {
                saveProgressToLocalStorage();
            }, 300);
        }
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [currentTime, isPlaying, isPaused, isLoading, isOpen, module?.id, saveProgressToLocalStorage, mode, duration]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isLoading && module?.id && isPlaying) {
            saveIntervalRef.current = setInterval(saveProgressToLocalStorage, 10000);
            return () => {
                if (saveIntervalRef.current) clearInterval(saveIntervalRef.current);
            };
        }
    }, [isOpen, isLoading, module?.id, isPlaying, saveProgressToLocalStorage, mode]);

    useEffect(() => {
        return () => {
            if (mode !== 'demo' && module?.id && isOpen) {
                saveProgressToLocalStorage();
            }
        };
    }, [module?.id, isOpen, saveProgressToLocalStorage, mode]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isPaused && !isLoading && isPlaying) {
            timerRef.current = setInterval(() => {
                setTimeSpent(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isOpen, isPaused, isLoading, isPlaying, mode]);

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
            setIsLoading(false);
            setVideoError(null);
            console.log('🎬 [VIDEO] Métadonnées chargées, durée:', videoRef.current.duration);
            
            const saved = localStorage.getItem(`module_progress_${module?.id}`);
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    if (data.currentTime && data.currentTime < videoRef.current.duration) {
                        videoRef.current.currentTime = data.currentTime;
                        setCurrentTime(data.currentTime);
                        console.log('🎬 [VIDEO] Position restaurée:', data.currentTime);
                    }
                } catch (e) {
                    console.error('Erreur restauration position:', e);
                }
            }
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
            const progressValue = Math.min(100, Math.round((videoRef.current.currentTime / videoRef.current.duration) * 100));
            setProgress(progressValue);
        }
    };

    const handleVideoEnd = () => {
        setIsPlaying(false);
        setProgress(100);
        console.log('🎬 [VIDEO] Vidéo terminée');
        saveProgressToLocalStorage();
    };

    const handleError = (e) => {
        console.error('🎬 [VIDEO] Erreur:', e);
        if (loadAttempts >= 2) {
            setIsLoading(false);
            setVideoError('Impossible de charger la vidéo. Vérifiez l\'URL.');
        } else {
            setLoadAttempts(prev => prev + 1);
            console.log(`🔄 [VIDEO] Tentative ${loadAttempts + 1}/3, nouvel essai...`);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.load();
                }
            }, 2000);
        }
    };

    const handleWaiting = () => {
        setIsBuffering(true);
    };

    const handleCanPlay = () => {
        setIsBuffering(false);
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                videoRef.current.play().catch(err => {
                    console.error('Erreur lecture:', err);
                    setVideoError('Impossible de lire la vidéo');
                });
                setIsPlaying(true);
            }
        }
    };

    const handleVolumeChange = (e) => {
        const val = parseFloat(e.target.value);
        setVolume(val);
        if (videoRef.current) {
            videoRef.current.volume = val;
            setIsMuted(val === 0);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            const newMuted = !isMuted;
            setIsMuted(newMuted);
            videoRef.current.muted = newMuted;
            if (newMuted) {
                setVolume(0);
            } else {
                setVolume(1);
                videoRef.current.volume = 1;
            }
        }
    };

    const handleSeek = (e) => {
        const seekTime = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const skipForward = () => {
        if (videoRef.current) {
            const newTime = Math.min(videoRef.current.currentTime + 10, duration);
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const skipBackward = () => {
        if (videoRef.current) {
            const newTime = Math.max(videoRef.current.currentTime - 10, 0);
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleModuleChange = (direction) => {
        if (mode !== 'demo') {
            saveProgressToLocalStorage();
        }
        if (onModuleChange) {
            if (onComplete && direction > 0) {
                onComplete(module?.id);
            }
            onModuleChange(direction);
            setModuleKey(null);
            setCurrentTime(0);
            setDuration(0);
            setTimeSpent(0);
            setIsLoading(true);
            setVideoError(null);
            setLoadAttempts(0);
            setIsPlaying(false);
        }
    };

    const goToPreviousModule = () => {
        if (moduleOrdre > 1) {
            handleModuleChange(-1);
        }
    };

    const goToNextModule = () => {
        if (moduleOrdre < totalModules) {
            handleModuleChange(1);
        }
    };

    const goToFirstPage = () => {
        if (currentTime > 0) {
            if (videoRef.current) {
                videoRef.current.currentTime = 0;
                setCurrentTime(0);
            }
        }
    };

    const handleTerminer = () => {
        if (mode === 'demo') {
            onClose();
            return;
        }
        if (module?.id) {
            const existing = localStorage.getItem(`module_progress_${module.id}`);
            let progressData = {};
            if (existing) {
                try { progressData = JSON.parse(existing); } catch (e) { console.error(e); }
            }
            progressData.completed = true;
            progressData.completedAt = new Date().toISOString();
            localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(progressData));
        }
        saveProgressToLocalStorage();
        if (onComplete) onComplete(module?.id);
        if (onClose) onClose();
    };

    const isLastModule = moduleOrdre >= totalModules;
    const isVideoComplete = progress >= 95 && duration > 0;
    const isComplete = isLastModule && isVideoComplete;

    if (!isOpen || !module) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
            <div ref={containerRef} className="relative w-full max-w-6xl h-[95vh] bg-black rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 sm:p-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
                        <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition"><X className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                            <BookOpen className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-200" />
                            <span className="font-serif font-bold text-[10px] sm:text-sm truncate">{formationTitre || 'Formation'}</span>
                            <span className="text-[8px] sm:text-xs text-blue-200 bg-blue-700/50 px-1.5 py-0.5 rounded-full">Module {moduleOrdre}/{totalModules}</span>
                        </div>
                        {mode === 'demo' && <span className="text-[8px] sm:text-xs bg-yellow-500/50 text-white px-1.5 py-0.5 rounded-full ml-1 sm:ml-2">🎯 Démo</span>}
                        {progress > 0 && mode !== 'demo' && (
                            <div className="flex items-center gap-0.5 sm:gap-1 ml-1 sm:ml-2">
                                <div className="w-12 sm:w-20 h-1.5 bg-blue-700/50 rounded-full overflow-hidden">
                                    <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                                </div>
                                <span className="text-[8px] sm:text-xs text-blue-200">{progress}%</span>
                            </div>
                        )}
                        {isComplete && mode !== 'demo' && (
                            <span className="text-[8px] sm:text-xs bg-yellow-400 text-blue-900 px-1.5 py-0.5 rounded-full font-bold ml-1 sm:ml-2">✅ Terminé !</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                        {mode !== 'demo' && (
                            <div className="flex items-center gap-0.5 sm:gap-1.5 bg-blue-700/50 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-200" />
                                <span className="font-mono text-[8px] sm:text-sm font-medium">{formatTime(timeSpent)}</span>
                            </div>
                        )}
                        <button onClick={() => setIsPaused(!isPaused)} className={`p-1.5 rounded-lg transition ${isPaused ? 'bg-yellow-500/50 hover:bg-yellow-500' : 'hover:bg-white/20'}`}>
                            {isPaused ? <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" /> : <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </button>
                        <button onClick={toggleFullscreen} className="p-1.5 hover:bg-white/20 rounded-lg transition">
                            {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> : <Maximize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
                        </button>
                    </div>
                </div>

                {/* Player */}
                <div className="flex-1 relative bg-black overflow-hidden">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
                            <div className="text-center px-4">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                <p className="mt-2 sm:mt-4 text-xs sm:text-sm text-white font-serif">Chargement de la vidéo...</p>
                                <p className="text-[8px] sm:text-xs text-gray-400 font-serif mt-1 break-all">{videoUrl || 'Aucune URL disponible'}</p>
                                <p className="text-[8px] sm:text-xs text-gray-400 font-serif mt-0.5">Tentative {loadAttempts + 1}/3</p>
                            </div>
                        </div>
                    )}
                    {isBuffering && !isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                    {videoError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black z-10 p-4">
                            <div className="text-center max-w-md w-full p-4 sm:p-6 bg-white/10 backdrop-blur-lg rounded-xl">
                                <div className="text-5xl sm:text-6xl mb-4">❌</div>
                                <p className="text-xs sm:text-sm text-red-400 font-serif font-semibold">{videoError}</p>
                                <p className="text-[8px] sm:text-xs text-gray-400 mt-1 font-mono break-all">URL: {videoUrl || 'Non définie'}</p>
                                <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center mt-3 sm:mt-4">
                                    <button 
                                        onClick={() => { 
                                            setIsLoading(true); 
                                            setVideoError(null); 
                                            setLoadAttempts(0); 
                                            if (videoRef.current) {
                                                videoRef.current.load();
                                            }
                                        }} 
                                        className="px-2 py-1 sm:px-4 sm:py-2 text-[8px] sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                    >
                                        🔄 Réessayer
                                    </button>
                                    <button 
                                        onClick={onClose} 
                                        className="px-2 py-1 sm:px-4 sm:py-2 text-[8px] sm:text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                                    >
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    <video
                        ref={videoRef}
                        className="w-full h-full object-contain"
                        onLoadedMetadata={handleLoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={handleVideoEnd}
                        onError={handleError}
                        onWaiting={handleWaiting}
                        onCanPlay={handleCanPlay}
                        playsInline
                        preload="metadata"
                    >
                        <source src={videoUrl} type="video/mp4" />
                        Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                </div>

                {/* Contrôles vidéo */}
                <div className="bg-gradient-to-t from-black/90 to-black/50 p-2 sm:p-3 flex flex-col gap-1 flex-shrink-0">
                    <div className="flex items-center gap-1.5 sm:gap-3 px-0.5 sm:px-1">
                        <span className="text-[8px] sm:text-xs text-white font-mono min-w-[30px] sm:min-w-[45px]">{formatTime(currentTime)}</span>
                        <input
                            type="range"
                            min={0}
                            max={duration || 0}
                            value={currentTime}
                            onChange={handleSeek}
                            className="flex-1 h-0.5 sm:h-1 bg-white/30 rounded-full appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, #3b82f6 ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.3) ${(currentTime / (duration || 1)) * 100}%)`
                            }}
                        />
                        <span className="text-[8px] sm:text-xs text-white/60 font-mono min-w-[30px] sm:min-w-[45px]">{formatTime(duration)}</span>
                    </div>

                    <div className="flex items-center justify-between px-0.5 sm:px-1">
                        <div className="flex items-center gap-0.5 sm:gap-1">
                            <button
                                onClick={togglePlay}
                                className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition"
                            >
                                {isPlaying ? <Pause className="w-4 h-4 sm:w-5 sm:h-5" /> : <Play className="w-4 h-4 sm:w-5 sm:h-5" />}
                            </button>
                            <button
                                onClick={skipBackward}
                                className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition"
                                title="Reculer de 10s"
                            >
                                <SkipBack className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                            </button>
                            <button
                                onClick={skipForward}
                                className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition"
                                title="Avancer de 10s"
                            >
                                <SkipForward className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                            </button>
                            <button
                                onClick={goToFirstPage}
                                disabled={currentTime === 0}
                                className="p-1.5 sm:p-2 text-white hover:bg-white/20 rounded-full transition disabled:opacity-30"
                                title="Revenir au début"
                            >
                                <Home className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                            </button>
                        </div>

                        <div className="flex items-center gap-1.5 sm:gap-3">
                            <div className="flex items-center gap-0.5 sm:gap-1">
                                <button
                                    onClick={toggleMute}
                                    className="p-1 text-white hover:bg-white/20 rounded-full transition"
                                >
                                    {isMuted ? <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                                </button>
                                <input
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="w-10 sm:w-16 h-0.5 sm:h-1 bg-white/30 rounded-full appearance-none cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2">
                            {isVideoComplete && mode !== 'demo' && (
                                <button
                                    onClick={handleTerminer}
                                    className="px-1.5 py-0.5 sm:px-3 sm:py-1.5 text-[7px] sm:text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition flex items-center gap-0.5 sm:gap-1"
                                >
                                    <LogOut className="w-3 h-3 sm:w-4 sm:h-4" /> Terminer
                                </button>
                            )}
                            {isVideoComplete && mode === 'demo' && (
                                <button
                                    onClick={onClose}
                                    className="px-1.5 py-0.5 sm:px-3 sm:py-1.5 text-[7px] sm:text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition flex items-center gap-0.5 sm:gap-1"
                                >
                                    <X className="w-3 h-3 sm:w-4 sm:h-4" /> Fermer
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-0.5 sm:pt-1 border-t border-white/10">
                        <div className="flex items-center gap-1 sm:gap-2">
                            <span className="text-[7px] sm:text-xs text-white/60 font-serif">Module {moduleOrdre}/{totalModules}</span>
                            {mode === 'demo' && (
                                <span className="text-[6px] sm:text-[10px] text-yellow-400 bg-yellow-500/20 px-1 py-0.5 rounded-full">🔍 Aperçu</span>
                            )}
                        </div>
                        <div className="flex items-center gap-0.5 sm:gap-2">
                            <button
                                onClick={goToPreviousModule}
                                disabled={moduleOrdre <= 1 || isLoading}
                                className="px-1.5 py-0.5 sm:px-3 sm:py-1 text-[7px] sm:text-xs text-white/60 hover:text-white hover:bg-white/10 rounded disabled:opacity-30 transition"
                            >
                                ◀ Précédent
                            </button>
                            <button
                                onClick={goToNextModule}
                                disabled={moduleOrdre >= totalModules || isLoading}
                                className="px-1.5 py-0.5 sm:px-3 sm:py-1 text-[7px] sm:text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-30 transition"
                            >
                                Suivant ▶
                            </button>
                            {mode !== 'demo' && (
                                <button
                                    onClick={saveProgressToLocalStorage}
                                    className="px-1.5 py-0.5 sm:px-3 sm:py-1 text-[7px] sm:text-xs bg-blue-500/30 text-blue-300 hover:bg-blue-500/50 rounded transition flex items-center gap-0.5 sm:gap-1"
                                    title="Sauvegarder la progression"
                                >
                                    💾 Sauvegarder
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}